import React, { useState, useEffect, useRef } from 'react';
import { adminCmsAPI } from '../../lib/cmsApi';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { toast } from 'sonner';
import {
  Upload, Image as ImageIcon, Trash2, Loader2,
  FolderOpen, Check, X,
} from 'lucide-react';

const ImagePicker = ({ value, onChange, folder = 'general', label = 'Image' }) => {
  const [showPicker, setShowPicker] = useState(false);
  const [mediaList, setMediaList] = useState([]);
  const [loadingMedia, setLoadingMedia] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const fetchMedia = async () => {
    setLoadingMedia(true);
    try {
      const res = await adminCmsAPI.listMedia();
      setMediaList((res.data || []).filter(m => m.file_type === 'image'));
    } catch { /* ignore */ }
    finally { setLoadingMedia(false); }
  };

  useEffect(() => {
    if (showPicker) fetchMedia();
  }, [showPicker]);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const res = await adminCmsAPI.uploadMedia(file, folder);
      const url = res.data.url;
      onChange(url);
      setShowPicker(false);
      toast.success('Image televersee');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur de televersement');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const selectMedia = (url) => {
    onChange(url);
    setShowPicker(false);
  };

  return (
    <div data-testid="image-picker">
      <Label>{label}</Label>

      {/* Current image preview or upload button */}
      {value ? (
        <div className="mt-1.5 relative group rounded-lg overflow-hidden border bg-neutral-50 inline-flex items-center gap-3 pr-2 w-full">
          <img
            src={value}
            alt=""
            className="w-16 h-16 object-cover flex-shrink-0"
            onError={e => { e.target.style.display = 'none'; }}
          />
          <span className="text-xs text-neutral-500 truncate flex-1 min-w-0">{value}</span>
          <div className="flex gap-1 flex-shrink-0">
            <Button type="button" variant="ghost" size="icon" className="h-7 w-7"
              onClick={() => setShowPicker(true)} data-testid="change-image-btn">
              <FolderOpen className="w-3.5 h-3.5 text-neutral-400" />
            </Button>
            <Button type="button" variant="ghost" size="icon" className="h-7 w-7 text-red-400 hover:text-red-600"
              onClick={() => onChange('')} data-testid="remove-image-btn">
              <Trash2 className="w-3.5 h-3.5" />
            </Button>
          </div>
        </div>
      ) : (
        <div
          className="mt-1.5 border-2 border-dashed rounded-lg p-4 text-center cursor-pointer hover:border-[#F9B438] hover:bg-[#F9B438]/5 transition-colors"
          onClick={() => setShowPicker(true)}
          data-testid="open-picker-btn"
        >
          <Upload className="w-5 h-5 text-neutral-300 mx-auto mb-1" />
          <p className="text-xs text-neutral-400">Cliquez pour choisir ou televerser une image</p>
        </div>
      )}

      {/* Media Picker Modal */}
      {showPicker && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40" onClick={() => setShowPicker(false)} />
          <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[70vh] flex flex-col z-10" data-testid="media-picker-modal">
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-3.5 border-b">
              <h3 className="font-heading font-semibold text-[#1D1D1B]">Choisir une image</h3>
              <div className="flex items-center gap-2">
                <label className="cursor-pointer">
                  <input ref={fileRef} type="file" accept="image/*" onChange={handleUpload} className="hidden" disabled={uploading} />
                  <Button type="button" variant="outline" size="sm" asChild disabled={uploading}>
                    <span>
                      {uploading ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Upload className="w-3.5 h-3.5 mr-1.5" />}
                      Televerser
                    </span>
                  </Button>
                </label>
                <Button type="button" variant="ghost" size="icon" className="h-8 w-8" onClick={() => setShowPicker(false)}>
                  <X className="w-4 h-4" />
                </Button>
              </div>
            </div>

            {/* URL manual input */}
            <div className="px-5 py-2 border-b bg-neutral-50">
              <div className="flex gap-2 items-center">
                <Input
                  placeholder="Ou collez une URL d'image..."
                  className="text-sm h-8"
                  onKeyDown={e => {
                    if (e.key === 'Enter' && e.target.value) {
                      onChange(e.target.value);
                      setShowPicker(false);
                    }
                  }}
                  data-testid="manual-url-input"
                />
                <span className="text-[10px] text-neutral-400 whitespace-nowrap">Entree pour valider</span>
              </div>
            </div>

            {/* Media Grid */}
            <div className="flex-1 overflow-y-auto p-4">
              {loadingMedia ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-6 h-6 animate-spin text-[#F9B438]" />
                </div>
              ) : mediaList.length === 0 ? (
                <div className="text-center py-12">
                  <ImageIcon className="w-10 h-10 text-neutral-200 mx-auto mb-2" />
                  <p className="text-sm text-neutral-400">Aucune image dans la mediatheque</p>
                  <p className="text-xs text-neutral-300 mt-1">Televersez votre premiere image</p>
                </div>
              ) : (
                <div className="grid grid-cols-4 sm:grid-cols-5 gap-2">
                  {mediaList.map(item => (
                    <button
                      key={item.id}
                      type="button"
                      onClick={() => selectMedia(item.url)}
                      className={`relative aspect-square rounded-lg overflow-hidden border-2 transition-all hover:ring-2 hover:ring-[#F9B438] ${value === item.url ? 'border-[#F9B438] ring-2 ring-[#F9B438]' : 'border-transparent'}`}
                      data-testid={`media-item-${item.id}`}
                    >
                      <img src={item.url} alt={item.alt_text || ''} className="w-full h-full object-cover" />
                      {value === item.url && (
                        <div className="absolute inset-0 bg-[#F9B438]/20 flex items-center justify-center">
                          <Check className="w-6 h-6 text-[#F9B438] drop-shadow-md" />
                        </div>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ImagePicker;
